/* eslint-disable react-hooks/exhaustive-deps */

import 'intersection-observer';
import { CustomPageComponent, CustomAppContext } from '../components/app/types';
import { useEffect } from 'react';
import App, { AppInitialProps, AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import { wrapper } from '../store';
import useAnalytics from '../shared/hooks/app/useAnalytics';
import { useApollo } from '@zoopla/apollo';
import { ApolloProvider } from '@apollo/client';
import { handleAuthPagesRouting } from '../shared/helpers';
import {
    OptimizelyProvider,
    useOptimizelyProviderProps,
    WithOptimizelyPageProps,
    withSSROptimizely
} from '@zoopla/optimizely';

import { useFetchUser } from 'shared/hooks/useFetchUser';
import { ThemeProvider } from '@blueprint/ui';
import { zooplaTheme } from '@blueprint/theme';
import { CacheProvider } from '@emotion/react';
import { cache } from '@emotion/css';
import { NudgesTracker } from '@zoopla/nudges-tracker';
import { getWebChromeLayout } from 'shared/components/Layout/WebChrome';
import { useStoragePageHistory } from 'shared/components/SearchMap/hooks/useStoragePageHistory';
import 'shared/globalThisPolyfill'; // Required for older browsers e.g. Safari < v13

import { globalStyles } from '@zoopla-eng/zds-components/themes';

const OptimizelyDevToggles = dynamic<Readonly<Record<string, never>>>(
    () => import('@zoopla/optimizely').then((mod) => mod.DevToggle),
    { ssr: false }
);

type ExtendedPageProps = {
    initialApolloState: Record<string, unknown>;
} & WithOptimizelyPageProps;

interface CustomAppProps extends AppProps<ExtendedPageProps> {
    Component: AppProps<ExtendedPageProps>['Component'] &
        CustomPageComponent<ExtendedPageProps>;
    pageProps: ExtendedPageProps;
}

const isDev = process.env.NODE_ENV === 'development';

function AppWrapper({ Component, pageProps }: CustomAppProps) {
    const getLayout = Component.getLayout || getWebChromeLayout;
    const optimizelyProviderProps = useOptimizelyProviderProps(pageProps);

    useAnalytics();
    useFetchUser();

    useEffect(() => {
        if (document) {
            document.documentElement.className = 'js';
        }
        NudgesTracker.setSession();
    }, []);
    const client = useApollo(pageProps.initialApolloState);

    useStoragePageHistory();

    useEffect(() => {
        if (!isDev) {
            Router.events.on('routeChangeStart', handleAuthPagesRouting);

            return () => {
                Router.events.off(
                    'routeChangeComplete',
                    handleAuthPagesRouting
                );
            };
        }
    }, [Router.events]);

    return (
        <OptimizelyProvider {...optimizelyProviderProps}>
            <CacheProvider value={cache}>
                <ThemeProvider theme={zooplaTheme}>
                    {globalStyles()}
                    {isDev && <OptimizelyDevToggles />}
                    <ApolloProvider client={client}>
                        {getLayout(<Component {...pageProps} />)}
                    </ApolloProvider>
                </ThemeProvider>
            </CacheProvider>
        </OptimizelyProvider>
    );
}

/**
 * This is a temporary implementation to allow easier adoption of Page level
 * withSSROptimizely in a future MR.
 */
AppWrapper.getInitialProps = async (
    appContext: CustomAppContext
): Promise<AppInitialProps> => {
    const appProps = await App.getInitialProps(appContext);
    const pageProps: ExtendedPageProps =
        await withSSROptimizely.getInitialProps(async () => ({
            ...appProps.pageProps
        }))(appContext['ctx']);
    return { ...appProps, pageProps };
};

const appWithRedux = wrapper.withRedux(AppWrapper);

export default appWithRedux;
